import React from "react";
import { captureException } from "@sentry/react";
import { capitalize } from "lodash";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { PAYWALL_EXPERIMENTS, useVariableValue } from "lib/featureFlags";
import { Opportunity } from "lib/generated/app-service-gql/graphql";
import { useCreateOpportunity } from "lib/hooks/api/opportunities/useCreateOpportunity";
import { useEntityOpportunities } from "lib/hooks/api/opportunities/useEntityOpportunities";
import { useOpportunityWorkflow } from "lib/hooks/api/opportunities/useOpportunityWorkflow";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { routes } from "../../../../lib/routes";
import { OpportunityEntityType } from "../../../../lib/types/graphQLEnums";
import { getDefaultStageId } from "../../create_opportunity/utils";
import { opportunitySchema } from "../../utils";
import type { SharedSaveProps } from "../types";

type UseSaveToOpportunityProps = SharedSaveProps & {
  onShowDrawer: () => void;
  opportunities?: Pick<Opportunity, "id">[];
  savedCount?: number;
  onOpportunitySave?: (optId: string) => void;
  trackingData?: EventData;
};

type UseSaveToOpportunityReturn = {
  isSaved: boolean;
  totalSaved: number;
  handleSaveClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => Promise<void>;
  handleCreateOpportunityFromDefaultValues: () => Promise<void>;
};

export function useSaveToOpportunity({
  entityIds,
  entityType,
  defaultOpportunityValues,
  opportunities,
  savedCount,
  onShowDrawer,
  onOpportunitySave,
  trackingData,
}: UseSaveToOpportunityProps): UseSaveToOpportunityReturn {
  const { logEvent } = useTracking();

  const isPaywallExperimentsOn = useVariableValue(PAYWALL_EXPERIMENTS, false);
  const subscription = useSubscription();
  const { openPaywallModal } = usePaywall();

  // Only fetch saved opportunities for the first entity
  // TODO: Enhance to support multiple entities as the current UI
  // only works with a single entity
  const { data: entityOpportunities } = useEntityOpportunities(entityIds[0], {
    enabled: !opportunities && savedCount === undefined,
  });

  const buyerId = defaultOpportunityValues?.entities?.find(
    (entity) => entity.entityType === OpportunityEntityType.Organisation,
  )?.entityId;

  const { refetch: refetchBuyerOpportunities } = useEntityOpportunities(buyerId || "", {
    enabled: false,
  });

  const { data: workflow } = useOpportunityWorkflow({
    enabled: !defaultOpportunityValues?.stageId,
  });

  const { mutate: createOpportunity } = useCreateOpportunity({
    onSuccess: (data, variables) => {
      const title =
        entityType === "notice"
          ? "New Opportunity created from notice"
          : `${capitalize(entityType)} saved to ${variables.opportunity.name}`;

      toast.message(title, {
        action: (
          <Button as="a" href={`${routes.opportunities}/${data.createOpportunity.id}`}>
            View
          </Button>
        ),
        // TODO: removing this for now there is a ticket to add this functionality back but without immediatley deleting the opportunity
        // cancel: {
        //   label: "Change",
        //   onClick: () => deleteOpportunity({ ids: [data.createOpportunity.id] }),
        // },
      });
      onOpportunitySave?.(data.createOpportunity.id);
    },
    onError: (error) => {
      captureException(error);
      toast.error("Failed to create opportunity");
    },
  });

  const handleCreateOpportunityFromDefaultValues = async () => {
    const stageId =
      workflow && !defaultOpportunityValues?.stageId
        ? getDefaultStageId(workflow, defaultOpportunityValues)
        : defaultOpportunityValues?.stageId;

    try {
      const opportunity = opportunitySchema.omit({ entities: true }).parse({
        name: defaultOpportunityValues?.name || "",
        stageId,
        value: defaultOpportunityValues?.value,
        assignedToId: defaultOpportunityValues?.assignedToId,
        closeDate: defaultOpportunityValues?.closeDate,
        existingExpiryDate: defaultOpportunityValues?.existingExpiryDate,
        submissionDate: defaultOpportunityValues?.submissionDate,
      });

      const { entities } = opportunitySchema.pick({ entities: true }).parse({
        entities: defaultOpportunityValues?.entities,
      });

      createOpportunity({
        opportunity,
        entities: { entities: entities || [] },
      });
      logEvent(EventNames.saveDataClicked, { "Data type": entityType.toUpperCase() });
    } catch (error) {
      captureException(error);
      toast.error("Failed to create opportunity");
    }
  };

  const handleSaveClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    logEvent(EventNames.saveDataClicked, { "Data type": entityType.toUpperCase() });

    if (isPaywallExperimentsOn && !subscription.hasDataTypes("AWARDS")) {
      return openPaywallModal("OPPORTUNITY", {
        strategyPaywall: true,
        trackingData,
      });
    }

    if (entityType === "notice" && buyerId) {
      const { data } = await refetchBuyerOpportunities();
      if (!data?.entityOpportunities?.length) {
        void handleCreateOpportunityFromDefaultValues();
        return;
      }
    }

    onShowDrawer();
  };

  // Calculate total saved count and saved state
  let totalSaved = 0;
  let isSaved = false;
  if (savedCount !== undefined) {
    totalSaved = savedCount;
    isSaved = totalSaved > 0;
  } else {
    const opportunitiesCount = opportunities?.length ?? 0;
    const entityOpportunitiesCount = entityOpportunities?.length ?? 0;
    totalSaved = opportunitiesCount + entityOpportunitiesCount;
    isSaved = totalSaved > 0;
  }

  return {
    isSaved,
    totalSaved,
    handleSaveClick,
    handleCreateOpportunityFromDefaultValues,
  };
}
